import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Monitor,
  Clipboard,
  Database,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Maximize,
  MoreHorizontal,
  MoreVertical,
  Circle,
  User,
  Settings,
  LogOut,
  Building,
  Users,
  Key,
} from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectWorkspaceAndFetchOrgMember,
  initializeWorkspace,
  fetchWorkspaces,
} from '@/store/workspaceSlice'
import { logout, clearTokens } from '@/store/authSlice'
import { designTokens } from '@/utils/design-tokens'
import AccountManagementModal from '@/components/ui/AccountManagementModal'
import PasswordChangeModal from '@/components/ui/PasswordChangeModal'
import OrganizationManagementModal from '@/components/ui/OrganizationManagementModal'
import WorkspaceManagementModal from '@/components/ui/WorkspaceManagementModal'
import { mergeMenuSettings } from './mergeMenuSettings'
import { useOrgFavicon, useOrgLogoUrl } from '@/hooks/useOrgLogo'
import type { MenuIconName } from './menuCatalog'

export interface MenuItem {
  id: string
  label: string
  icon?: React.ReactNode
  children?: MenuItem[]
  isActive?: boolean
  onClick?: () => void
  path?: string
}

export interface SidebarProps {
  isExpanded?: boolean
  onToggle?: () => void
  menuItems?: MenuItem[]
  userName?: string
  workspaceName?: string
  workspaceYear?: string
  onFullScreen?: () => void
  onAspectRatio?: () => void
  onMoreOptions?: () => void
  className?: string
}

const Sidebar: React.FC<SidebarProps> = ({
                                           isExpanded = true,
                                           onToggle,
                                           menuItems,
                                           userName,
                                           workspaceName = '',
                                           workspaceYear = '',
                                           onFullScreen,
                                           onAspectRatio,
                                           onMoreOptions,
                                           className = '',
                                         }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  // Redux state
  const { workspaces, selectedWorkspace, isLoading } = useAppSelector(state => state.workspace)
  const { user, orgMember } = useAppSelector(state => state.auth)
  // menu_settings는 workspace get/list 응답에 함께 들어옴 (별도 GET 없음)
  const menuSettings = selectedWorkspace?.menu_settings ?? null
  // 기관 관리에서 올린 로고. 없거나 불러오지 못하면 기본 DOT 로고를 쓴다.
  const orgLogoUrl = useOrgLogoUrl()
  // 브라우저 탭 아이콘도 기관 타이틀 로고로 (사이드바는 로그인 화면마다 항상 그려진다)
  useOrgFavicon()
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null)
  const showOrgLogo = !!orgLogoUrl && orgLogoUrl !== failedLogoUrl

  // Check if user has admin privileges (owner or admin role)
  const hasAdminPrivileges = orgMember?.role === 'owner' || orgMember?.role === 'admin'

  // Local state for workspace dropdown
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false)

  // Local state for user settings hover menu
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)

  // Local state for account management modal
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false)

  // Local state for password change modal
  const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false)

  // Local state for organization management modal
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false)

  // Local state for workspace management modal
  const [isWorkspaceManagementModalOpen, setIsWorkspaceManagementModalOpen] = useState(false)

  // Local state for collapsed navigation tooltip
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number } | null>(null)

  // Local state for temporary expansion
  const [isTemporarilyExpanded, setIsTemporarilyExpanded] = useState(false)
  const [tempExpandTimer, setTempExpandTimer] = useState<NodeJS.Timeout | null>(null)


  // 1depth 카탈로그 아이콘 식별자 → 실제 lucide 컴포넌트 매핑
  const resolveIcon = (iconName: MenuIconName): React.ReactNode => {
    switch (iconName) {
      case 'Monitor':
        return <Monitor className="w-5 h-5" strokeWidth={2} />
      case 'Clipboard':
        return <Clipboard className="w-5 h-5" strokeWidth={2} />
      case 'Database':
        return <Database className="w-5 h-5" strokeWidth={2} />
    }
  }

  // 현재 경로와 매칭 여부 (내 현황은 루트 '/'도 활성으로 간주 — 기존 동작 유지)
  const isPathActive = (path: string): boolean => {
    if (path === '/dashboard/mytask') {
      return location.pathname === '/dashboard/mytask' || location.pathname === '/'
    }
    return location.pathname === path
  }

  // 카탈로그 + 백엔드 menu_settings 머지 → 최종 사이드바 메뉴
  const mergedMenuItems = useMemo(
    () =>
      mergeMenuSettings(
        menuSettings,
        hasAdminPrivileges,
        resolveIcon,
        (path) => navigate(path),
        isPathActive,
      ),
    [menuSettings, hasAdminPrivileges, navigate, location.pathname],
  )

  const finalMenuItems = menuItems || mergedMenuItems

  const [expandedSections, setExpandedSections] = useState<string[]>(['status', 'project', 'datacenter'])

  // Initialize workspace on component mount
  useEffect(() => {
    dispatch(initializeWorkspace())
    // fetchWorkspaces is now called in Layout component to ensure proper timing
  }, [dispatch])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isWorkspaceDropdownOpen) {
        const target = event.target as Element
        if (!target.closest('.workspace-dropdown')) {
          setIsWorkspaceDropdownOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isWorkspaceDropdownOpen])

  // Handle workspace selection
  const handleWorkspaceSelect = (workspace: any) => {
    dispatch(selectWorkspaceAndFetchOrgMember(workspace))
    setIsWorkspaceDropdownOpen(false)
  }

  // Get display values for workspace
  const displayWorkspaceName = selectedWorkspace?.org_name || workspaceName
  const displayWorkspaceYear = selectedWorkspace?.name || workspaceYear

  // Logout handler
  const handleLogout = async () => {
    try {
      const result = await dispatch(logout()).unwrap()
      // Navigate to root path after successful logout
      navigate('/', { replace: true })
    } catch (error) {
      console.error('Logout failed:', error)
      // Force logout even if API fails
      clearTokens()
      // Navigate to root path even if API fails
      navigate('/', { replace: true })
      // The rejected case in authSlice should handle state reset
    }
  }

  // Account management handlers
  const handleOpenAccountModal = () => {
    setIsUserMenuOpen(false) // Close hover menu
    setIsAccountModalOpen(true)
  }

  const handleCloseAccountModal = () => {
    setIsAccountModalOpen(false)
  }

  const handleAccountUpdateSuccess = (updatedUser: any) => {
    // Optionally update user state in Redux
    console.log('Account updated successfully:', updatedUser)
  }

  // Password change management handlers
  const handleOpenPasswordChangeModal = () => {
    setIsUserMenuOpen(false) // Close hover menu
    setIsPasswordChangeModalOpen(true)
  }

  const handleClosePasswordChangeModal = () => {
    setIsPasswordChangeModalOpen(false)
  }

  const handlePasswordChangeSuccess = () => {
    console.log('Password changed successfully')
    // Any additional logic when password is changed successfully
  }

  // Organization management handlers
  const handleCloseOrganizationModal = () => {
    setIsOrganizationModalOpen(false)
  }

  const handleOrganizationUpdateSuccess = () => {
    console.log('Organization updated successfully')
    // Any additional logic when organization is updated successfully
  }

  // Workspace management modal handlers
  const handleOpenWorkspaceManagementModal = () => {
    setIsWorkspaceDropdownOpen(false)
    setIsWorkspaceManagementModalOpen(true)
  }

  const handleCloseWorkspaceManagementModal = () => {
    setIsWorkspaceManagementModalOpen(false)
  }

  const handleWorkspaceManagementSuccess = () => {
    dispatch(fetchWorkspaces({}))
  }

  const toggleSection = (id: string) => {
    setExpandedSections(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id],
    )
  }

  // Collapsed navigation handlers
  const handleMenuAreaEnter = (itemId: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setHoveredMenuItem(itemId)
    setTooltipPosition({ top: rect.top + rect.height / 2 - window.scrollY })
  }

  const handleMenuAreaLeave = () => {
    setHoveredMenuItem(null)
    setTooltipPosition(null)
  }

  // Check if a parent item has any active children
  const getParentActiveState = (item: MenuItem) => {
    return item.children?.some(child => child.isActive) || false
  }

  // Temporary expansion handlers (for mobile/touch devices)
  const handleTemporaryExpand = (_itemId: string) => {
    if (tempExpandTimer) {
      clearTimeout(tempExpandTimer)
    }

    setIsTemporarilyExpanded(true)

    // Auto-collapse after 3 seconds
    const timer = setTimeout(() => {
      setIsTemporarilyExpanded(false)
    }, 3000)

    setTempExpandTimer(timer)
  }

  const handleCloseTemporaryExpand = () => {
    if (tempExpandTimer) {
      clearTimeout(tempExpandTimer)
      setTempExpandTimer(null)
    }
    setIsTemporarilyExpanded(false)
  }

  // Clean up timer on component unmount
  useEffect(() => {
    return () => {
      if (tempExpandTimer) {
        clearTimeout(tempExpandTimer)
      }
    }
  }, [tempExpandTimer])

  return (
    <div
      className={`
        flex flex-col
        transition-all duration-300 ease-in-out
        ${isExpanded || isTemporarilyExpanded ? 'w-[270px]' : 'w-[70px]'}
        h-full
        ${className}
      `}
      style={{
        backgroundColor: designTokens.colors.primary.dark,
        color: designTokens.colors.gray[0],
      }}
    >
      {/* Logo Section - Fixed Height */}
      {isExpanded ? (
        <div className="flex-shrink-0 h-[70px] px-4">
          <div className="flex justify-between items-center h-full">
            {showOrgLogo ? (
              <div className="flex items-center h-full overflow-hidden mr-2">
                <img
                  src={orgLogoUrl!}
                  alt={selectedWorkspace?.org_name ? `${selectedWorkspace.org_name} 로고` : '기관 로고'}
                  className="max-w-[180px] max-h-[48px] object-contain transition-opacity duration-300 delay-150"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                  }}
                  onError={() => setFailedLogoUrl(orgLogoUrl)}
                />
              </div>
            ) : (
            <div className="flex items-end gap-[7px] overflow-hidden">
              <img
                src="/images/logo-big.png"
                alt="Logo"
                className="w-[92px] h-[40px] object-contain transition-opacity duration-300 delay-150"
                style={{
                  opacity: isExpanded ? 1 : 0,
                }}
              />
              <div
                className="transition-opacity duration-300 delay-150 whitespace-nowrap"
                style={{
                  color: designTokens.colors.gray[0],
                  fontSize: designTokens.typography.fontSize['logo-text'][0],
                  fontWeight: designTokens.typography.fontSize['logo-text'][1].fontWeight,
                  lineHeight: designTokens.typography.fontSize['logo-text'][1].lineHeight,
                  fontFamily: designTokens.typography.fontFamily.logo[0],
                  opacity: isExpanded ? 1 : 0,
                }}
              >
                성과관리
              </div>
            </div>
            )}
            <button
              onClick={onToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onToggle?.()
                }
              }}
              className="relative group w-8 h-8 focus:outline-none focus:ring-2 focus:ring-opacity-50 rounded-md transition-all duration-200 hover:scale-110 focus:scale-110 flex items-center justify-center border"
              style={{
                backgroundColor: designTokens.colors.primary.DEFAULT,
                borderColor: designTokens.colors.gray[4],
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.light
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.DEFAULT
              }}
              onFocus={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.light
                e.currentTarget.style.borderColor = designTokens.colors.gray[0]
              }}
              onBlur={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.DEFAULT
                e.currentTarget.style.borderColor = designTokens.colors.gray[4]
              }}
              aria-label="사이드바 접기"
              title="사이드바 접기"
              tabIndex={0}
            >
              <ChevronLeft
                className="w-5 h-5 transition-transform group-hover:translate-x-[-1px] group-focus:translate-x-[-1px]"
                strokeWidth={2.5}
                style={{ color: designTokens.colors.gray[0] }}
              />

              {/* Tooltip */}
              <div
                className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 text-sm rounded shadow-lg opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999]"
                style={{
                  backgroundColor: designTokens.colors.gray[6],
                  color: designTokens.colors.white,
                }}
              >
                사이드바 접기
                <div
                  className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent"
                  style={{ borderBottomColor: designTokens.colors.gray[6] }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0 h-[100px] px-4">
          <div className="flex flex-col items-center justify-start h-full pt-4 gap-5">
            <img
              src="/images/logo-small.png"
              alt="Logo"
              className="w-[40px] h-[40px] object-contain"
            />
            <button
              onClick={onToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onToggle?.()
                }
              }}
              className="relative group w-8 h-8 focus:outline-none focus:ring-2 focus:ring-opacity-50 rounded-md transition-all duration-200 hover:scale-110 focus:scale-110 flex items-center justify-center border"
              style={{
                backgroundColor: designTokens.colors.primary.DEFAULT,
                borderColor: designTokens.colors.gray[4],
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.light
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.DEFAULT
              }}
              onFocus={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.light
                e.currentTarget.style.borderColor = designTokens.colors.gray[0]
              }}
              onBlur={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.primary.DEFAULT
                e.currentTarget.style.borderColor = designTokens.colors.gray[4]
              }}
              aria-label="사이드바 펼치기"
              title="사이드바 펼치기"
              tabIndex={0}
            >
              <ChevronRight
                className="w-5 h-5 transition-transform group-hover:translate-x-[1px] group-focus:translate-x-[1px]"
                strokeWidth={2.5}
                style={{ color: designTokens.colors.gray[0] }}
              />

              {/* Tooltip */}
              <div
                className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 text-sm rounded shadow-lg opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999]"
                style={{
                  backgroundColor: designTokens.colors.gray[6],
                  color: designTokens.colors.white,
                }}
              >
                사이드바 펼치기
                <div
                  className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent"
                  style={{ borderRightColor: designTokens.colors.gray[6] }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Navigation Section - Scrollable */
      }
      <div className={`flex-1 ${isExpanded || isTemporarilyExpanded ? 'overflow-y-auto px-4 py-[10px]' : 'overflow-visible py-5'}`}>
        {isExpanded || isTemporarilyExpanded ? (
          // Expanded Navigation
          <div className={`space-y-[10px] transition-opacity duration-300 delay-150 overflow-hidden ${isExpanded || isTemporarilyExpanded ? 'opacity-100' : 'opacity-0'}`}>
            {isTemporarilyExpanded && (
              <div className="flex items-center justify-between mb-4 p-2 bg-[#2F4062] rounded">
                <span className="text-sm text-[#F6F6F7]"
                      style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}>
                  임시 확장됨 (3초 후 자동 접기)
                </span>
                <button
                  onClick={handleCloseTemporaryExpand}
                  className="text-[#F6F6F7] hover:text-[#6CACE4] transition-colors"
                  aria-label="수동으로 접기"
                >
                  ✕
                </button>
              </div>
            )}
            {finalMenuItems.map((item, index) => (
              <div key={item.id}
                   className={`${index < finalMenuItems.length - 1 ? 'border-b border-[#EBEBEB] pb-[7px]' : ''}`}>
                {/* Main Menu Item */}
                <div
                  className="flex items-center justify-between w-full py-[10px] px-[1px] cursor-pointer rounded transition-colors"
                  style={{
                    '&:hover': { backgroundColor: designTokens.colors.primary.DEFAULT },
                  } as any}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = designTokens.colors.primary.DEFAULT
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                  onClick={() => item.children && item.children.length > 0 && toggleSection(item.id)}
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div className="w-5 h-5 flex-shrink-0" style={{ color: designTokens.colors.gray[0] }}>
                      {item.icon}
                    </div>
                    <span
                      className="whitespace-nowrap"
                      style={{
                        color: designTokens.colors.gray[0],
                        fontSize: designTokens.typography.fontSize['title-xsmall'][0],
                        fontWeight: designTokens.typography.fontSize['title-xsmall'][1].fontWeight,
                        lineHeight: designTokens.typography.fontSize['title-xsmall'][1].lineHeight,
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center">
                    {item.children && item.children.length > 0 && (
                      expandedSections.includes(item.id) ?
                        <ChevronUp
                          className="w-4 h-4"
                          strokeWidth={2}
                          style={{ color: designTokens.colors.gray[0] }}
                        /> :
                        <ChevronDown
                          className="w-4 h-4"
                          strokeWidth={2}
                          style={{ color: designTokens.colors.gray[0] }}
                        />
                    )}
                  </div>
                </div>

                {/* Sub Menu Items */}
                {item.children && expandedSections.includes(item.id) && (
                  <div className="space-y-[3px]">
                    {item.children.map((child) => (
                      <a
                        key={child.id}
                        href={child.path || '#'}
                        className="flex items-center w-full py-[4px] px-[18px] cursor-pointer rounded transition-colors no-underline"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = designTokens.colors.primary.DEFAULT
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                        onClick={(e) => {
                          // Allow Cmd+Click (Mac), Ctrl+Click (Windows), middle-click, and right-click to work as normal links
                          if (e.metaKey || e.ctrlKey || e.button === 1 || e.button === 2) {
                            return
                          }
                          e.preventDefault()
                          child.onClick && child.onClick()
                        }}
                      >
                        <Circle
                          className={`w-[5px] h-[5px] mr-[10px] ${child.isActive ? 'fill-current' : 'fill-transparent'}`}
                          strokeWidth={5}
                          style={{
                            color: designTokens.colors.gray[0],
                            stroke: designTokens.colors.gray[0],
                          }}
                        />
                        <div
                          className={`px-[1px] py-[3px] overflow-hidden`}
                          style={{
                            borderBottom: child.isActive ? `1px solid ${designTokens.colors.gray[0]}` : 'none',
                          }}
                        >
                          <span
                            className="whitespace-nowrap"
                            style={{
                              color: designTokens.colors.gray[0],
                              fontSize: designTokens.typography.fontSize['body-large'][0],
                              fontWeight: child.isActive ? '600' : designTokens.typography.fontSize['body-large'][1].fontWeight,
                              lineHeight: designTokens.typography.fontSize['body-large'][1].lineHeight,
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                            }}
                          >
                            {child.label}
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Collapsed Navigation - Icons with Hover Tooltips
          <div className="flex flex-col items-center gap-5 relative">
            {finalMenuItems.map((item) => (
              <div
                key={item.id}
                className={`
                  relative flex justify-center items-center w-full py-[10px] transition-colors cursor-pointer rounded
                  ${getParentActiveState(item) ? 'bg-[#2F4062]' : 'hover:bg-[#2F4062]'}
                `}
                onMouseEnter={(e) => item.children && item.children.length > 0 && handleMenuAreaEnter(item.id, e)}
                onClick={() => {
                  // If item has children, navigate to first child; otherwise use item's onClick
                  if (item.children && item.children.length > 0) {
                    const firstChild = item.children[0]
                    if (firstChild.onClick) {
                      firstChild.onClick()
                    }
                  } else if (item.onClick) {
                    item.onClick()
                  }
                }}
                onDoubleClick={() => {
                  // Double-click to temporarily expand (alternative for touch devices)
                  if (item.children && item.children.length > 0) {
                    handleTemporaryExpand(item.id)
                  }
                }}
              >
                <div className="w-[30px] h-[30px] flex items-center justify-center text-[#F6F6F7] relative">
                  {React.cloneElement(item.icon as React.ReactElement, {
                    className: 'w-[25px] h-[22.5px]',
                    strokeWidth: 2,
                  })}
                  {/* Active indicator */}
                  {getParentActiveState(item) && (
                    <div className="absolute top-0 right-0 w-2 h-2 bg-[#6CACE4] rounded-full"></div>
                  )}
                </div>
              </div>
            ))}

            {/* Tooltip Submenu with unified hover area */}
            {hoveredMenuItem && tooltipPosition && (
              <div
                className="fixed z-40"
                style={{
                  left: '0px',
                  top: tooltipPosition.top - 50,
                  width: '300px',
                  height: '100px',
                }}
                onMouseLeave={handleMenuAreaLeave}
              >
                {/* Tooltip content */}
                <div
                  className="sidebar-tooltip absolute bg-[#2F4062] border border-[#EBEBEB] rounded-lg shadow-lg z-50"
                  style={{
                    left: '75px',
                    top: '50px',
                    minWidth: '200px',
                    transform: 'translateY(-50%)',
                  }}
                >
                  {(() => {
                    const hoveredItem = finalMenuItems.find(item => item.id === hoveredMenuItem)
                    if (!hoveredItem || !hoveredItem.children) return null

                    return (
                      <>
                        {/* Parent Category Header */}
                        <div className="px-3 py-2 border-b border-[#EBEBEB]">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 text-[#F6F6F7]">
                              {React.cloneElement(hoveredItem.icon as React.ReactElement, {
                                className: 'w-4 h-4',
                                strokeWidth: 2,
                              })}
                            </div>
                            <span
                              className="text-sm font-semibold text-[#F6F6F7]"
                              style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
                            >
                            {hoveredItem.label}
                          </span>
                          </div>
                        </div>

                        {/* Child Menu Items */}
                        <div className="py-1">
                          {hoveredItem.children.map((child) => (
                            <a
                              key={child.id}
                              href={child.path || '#'}
                              className="flex items-center px-3 py-2 hover:bg-[#141E2F] cursor-pointer transition-colors no-underline"
                              onClick={(e) => {
                                // Allow Cmd+Click (Mac), Ctrl+Click (Windows), middle-click, and right-click to work as normal links
                                if (e.metaKey || e.ctrlKey || e.button === 1 || e.button === 2) {
                                  return
                                }
                                e.preventDefault()
                                if (child.onClick) {
                                  child.onClick()
                                }
                                setHoveredMenuItem(null)
                                setTooltipPosition(null)
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <div
                                  className={`w-2 h-2 rounded-full ${child.isActive ? 'bg-[#6CACE4]' : 'bg-transparent border border-[#EBEBEB]'}`}></div>
                                <span
                                  className={`text-sm ${child.isActive ? 'font-semibold' : 'font-normal'} text-[#F6F6F7]`}
                                  style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
                                >
                                {child.label}
                              </span>
                              </div>
                            </a>
                          ))}
                        </div>
                      </>
                    )
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Section - Fixed Height */
      }
      <div className={`flex-shrink-0 ${isExpanded || isTemporarilyExpanded ? 'h-[225px]' : 'h-[110px]'} flex flex-col`}>
        {isExpanded || isTemporarilyExpanded ? (
          <div className="flex flex-col h-full">
            {/* Workspace Selector */}
            <div
              className={`flex-shrink-0 flex flex-col px-3 py-2 gap-1 h-[75px] justify-center relative workspace-dropdown transition-opacity duration-300 delay-150 ${isExpanded || isTemporarilyExpanded ? 'opacity-100' : 'opacity-0'}`}>
              <div className="text-sm font-bold text-[#F6F6F7]"
                   style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                워크스페이스
              </div>
              <div
                className="flex items-center justify-between border border-[#EBEBEB] rounded px-2 py-1 gap-2 cursor-pointer hover:border-[#F6F6F7] transition-colors"
                onClick={() => setIsWorkspaceDropdownOpen(!isWorkspaceDropdownOpen)}
              >
                <div className="flex flex-col px-[4px] py-0.5 gap-0.5">
                  <span className="text-base font-bold text-[#F6F6F7]"
                        style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                    {displayWorkspaceYear}
                  </span>
                  <span className="text-xs font-normal text-[#F6F6F7]"
                        style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                    {displayWorkspaceName}
                  </span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-[#F6F6F7] transition-transform ${isWorkspaceDropdownOpen ? 'rotate-180' : ''}`}
                  strokeWidth={2}
                />
              </div>

              {/* Workspace Dropdown */}
              {isWorkspaceDropdownOpen && (
                <div
                  className="absolute bottom-full left-3 right-3 mb-1 bg-[#141E2F] border border-[#EBEBEB] rounded shadow-lg z-50 flex flex-col"
                  style={{ maxHeight: '320px' }}
                >
                  {/* 워크스페이스 목록 (스크롤 영역) */}
                  <div className="overflow-y-auto flex-1">
                    {isLoading ? (
                      <div className="px-3 py-2 text-sm text-[#F6F6F7]">로딩 중...</div>
                    ) : workspaces.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-[#F6F6F7]">워크스페이스가 없습니다.</div>
                    ) : (
                      workspaces.map((workspace) => (
                        <div
                          key={workspace.id}
                          className={`px-3 py-2 cursor-pointer hover:bg-[#2F4062] transition-colors ${
                            selectedWorkspace?.id === workspace.id ? 'bg-[#2F4062]' : ''
                          }`}
                          onClick={() => handleWorkspaceSelect(workspace)}
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              {workspace.name}
                            </span>
                            <span className="text-xs text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              {workspace.org_name}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* 워크스페이스 관리 버튼 (항상 하단에 고정) */}
                  <div className="border-t border-[#6E6E6E] flex-shrink-0">
                    <div
                      className="px-3 py-2 cursor-pointer hover:bg-[#2F4062] transition-colors flex items-center gap-2"
                      onClick={handleOpenWorkspaceManagementModal}
                    >
                      <Settings className="w-4 h-4 text-[#F6F6F7]" />
                      <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                        워크스페이스 관리
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Spacing */}
            <div className="h-[15px]"></div>

            {/* User Profile with Settings Menu Container */}
            <div
              className={`flex-shrink-0 relative transition-opacity duration-300 delay-150 ${isExpanded || isTemporarilyExpanded ? 'opacity-100' : 'opacity-0'}`}
              onMouseEnter={() => setIsUserMenuOpen(true)}
              onMouseLeave={() => setIsUserMenuOpen(false)}
            >
              {/* Settings Hover Menu */}
              {isUserMenuOpen && (
                <div className="absolute bottom-[70px] left-0 right-0 pb-2 z-50">
                  <div className="bg-[#2F4062] border border-[#EBEBEB] rounded-lg shadow-lg overflow-hidden">
                    {/* 일반 설정 섹션 */}
                    <div className="px-3 py-2 border-b border-[#EBEBEB]">
                      <div className="text-sm font-semibold text-[#F6F6F7] mb-2"
                           style={{ fontFamily: 'Pretendard GOV' }}>
                        일반 설정
                      </div>
                      <div className="space-y-1">
                        <div
                          className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                          onClick={handleOpenAccountModal}
                        >
                          <User className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                          <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                            계정 관리
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                          onClick={handleOpenPasswordChangeModal}
                        >
                          <Key className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                          <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                            비밀번호 변경
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                          onClick={handleLogout}
                        >
                          <LogOut className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                          <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                            로그아웃
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 기관 설정 섹션 - Only show for owner/admin roles */}
                    {hasAdminPrivileges && (
                      <div className="px-3 py-2">
                        <div className="text-sm font-semibold text-[#F6F6F7] mb-2"
                             style={{ fontFamily: 'Pretendard GOV' }}>
                          기관 설정
                        </div>
                        <div className="space-y-1">
                          <div
                            className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                            onClick={() => {
                              setIsUserMenuOpen(false) // Close hover menu
                              setIsOrganizationModalOpen(true)
                            }}
                          >
                            <Building className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                            <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              기관 관리
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                            onClick={() => navigate('/setting/member')}
                          >
                            <Users className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                            <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              멤버 관리
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                            onClick={() => navigate('/setting/workspace')}
                          >
                            <Settings className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                            <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              워크스페이스 관리
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* User Profile */}
              <div
                className="flex items-center gap-[10px] px-[15px] py-[8px] h-[70px] justify-center cursor-pointer hover:bg-[#2F4062] transition-colors">
                <div className="w-[50px] h-[50px] rounded-full bg-[#D9D9D9]"></div>
                <div className="flex flex-col justify-center gap-[5px] flex-1">
                  <span className="text-base font-normal text-[#F6F6F7]"
                        style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                    {user?.name || userName || '사용자'}
                  </span>
                  <span className="text-base font-normal text-[#F6F6F7]"
                        style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                    설정
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex border-t border-[#EBEBEB] gap-[5px] h-[65px] transition-opacity duration-300 delay-150 ${isExpanded || isTemporarilyExpanded ? 'opacity-100' : 'opacity-0'}`}>
              <button
                onClick={onFullScreen}
                className="flex flex-col justify-center items-center flex-1 gap-1 hover:bg-gray-700 transition-colors"
              >
                <Maximize2 className="w-[15px] h-[15px]" strokeWidth={2} />
                <span className="text-base font-semibold text-[#F6F6F7]"
                      style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                  전체화면
                </span>
              </button>
              <button
                onClick={onAspectRatio}
                className="flex flex-col justify-center items-center flex-1 gap-1 hover:bg-gray-700 transition-colors"
              >
                <Maximize className="w-[15px] h-[15px]" strokeWidth={2} />
                <span className="text-base font-semibold text-[#F6F6F7]"
                      style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                  화면비율
                </span>
              </button>
              <button
                onClick={onMoreOptions}
                className="flex flex-col justify-center items-center flex-1 gap-1 hover:bg-gray-700 transition-colors"
              >
                {/*TODO: 임시 비활성화*/}
                {/*<MoreHorizontal className="w-[15px] h-[15px]" strokeWidth={2} />*/}
                {/*<span className="text-base font-semibold text-[#F6F6F7]"*/}
                {/*      style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>*/}
                {/*  더보기*/}
                {/*</span>*/}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Collapsed User Profile Container */}
            <div className="flex-shrink-0 relative">
              {/* Unified hover area for profile menu */}
              {isUserMenuOpen && (
                <div
                  className="fixed z-40"
                  style={{
                    left: '0px',
                    bottom: '0px',
                    width: '320px',
                    height: '200px',
                  }}
                  onMouseLeave={() => setIsUserMenuOpen(false)}
                >
                  {/* Settings Menu positioned within the hover area */}
                  <div
                    className="absolute bg-[#2F4062] border border-[#EBEBEB] rounded-lg shadow-lg overflow-hidden z-50"
                    style={{
                      bottom: '40px',
                      left: '75px',
                      width: '192px',
                    }}
                  >
                    {/* 일반 설정 섹션 */}
                    <div className="px-3 py-2 border-b border-[#EBEBEB]">
                      <div className="text-sm font-semibold text-[#F6F6F7] mb-2"
                           style={{ fontFamily: 'Pretendard GOV' }}>
                        일반 설정
                      </div>
                      <div className="space-y-1">
                        <div
                          className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                          onClick={handleOpenAccountModal}
                        >
                          <User className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                          <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                            계정 관리
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                          onClick={handleOpenPasswordChangeModal}
                        >
                          <Key className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                          <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                            비밀번호 변경
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                          onClick={handleLogout}
                        >
                          <LogOut className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                          <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                            로그아웃
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 기관 설정 섹션 - Only show for owner/admin roles */}
                    {hasAdminPrivileges && (
                      <div className="px-3 py-2">
                        <div className="text-sm font-semibold text-[#F6F6F7] mb-2"
                             style={{ fontFamily: 'Pretendard GOV' }}>
                          기관 설정
                        </div>
                        <div className="space-y-1">
                          <div
                            className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                            onClick={() => {
                              setIsUserMenuOpen(false) // Close hover menu
                              setIsOrganizationModalOpen(true)
                            }}
                          >
                            <Building className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                            <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              기관 관리
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                            onClick={() => navigate('/setting/member')}
                          >
                            <Users className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                            <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              멤버 관리
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-2 px-2 py-1 hover:bg-[#141E2F] rounded cursor-pointer transition-colors"
                            onClick={() => navigate('/setting/workspace')}
                          >
                            <Settings className="w-4 h-4 text-[#F6F6F7]" strokeWidth={2} />
                            <span className="text-sm text-[#F6F6F7]" style={{ fontFamily: 'Pretendard GOV' }}>
                              워크스페이스 관리
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Collapsed User Profile */}
              <div
                className="flex justify-center px-[15px] py-[15px] h-18 cursor-pointer hover:bg-[#2F4062] transition-colors"
                onMouseEnter={() => setIsUserMenuOpen(true)}
              >
                <div className="w-[45px] h-[45px] rounded-full bg-[#D9D9D9]"></div>
              </div>
            </div>

            {/* Collapsed Action Button */}
            <div className="border-t-[0.5px] border-[#EBEBEB] h-16 flex items-center justify-center">
              <button
                onClick={onMoreOptions}
                className="flex flex-col justify-center items-center w-full h-full gap-[5px] hover:bg-gray-700 transition-colors"
              >
                <MoreVertical className="w-[2px] h-[16px]" strokeWidth={2} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Account Management Modal */
      }
      <AccountManagementModal
        isOpen={isAccountModalOpen}
        onClose={handleCloseAccountModal}
        user={user}
        onSuccess={handleAccountUpdateSuccess}
      />

      {/* Password Change Modal */
      }
      <PasswordChangeModal
        isOpen={isPasswordChangeModalOpen}
        onClose={handleClosePasswordChangeModal}
        onSuccess={handlePasswordChangeSuccess}
      />

      {/* Organization Management Modal */
      }
      <OrganizationManagementModal
        isOpen={isOrganizationModalOpen}
        onClose={handleCloseOrganizationModal}
        onSuccess={handleOrganizationUpdateSuccess}
      />

      {/* Workspace Management Modal */}
      <WorkspaceManagementModal
        isOpen={isWorkspaceManagementModalOpen}
        onClose={handleCloseWorkspaceManagementModal}
        onSuccess={handleWorkspaceManagementSuccess}
      />
    </div>
  )
}

export default Sidebar